import { createTheme } from '@mantine/core'

export const getBadgeStyle = (status: string) => ({
  backgroundColor:
    status === 'New'
      ? '#f39c12'
      : status === 'Reserved'
        ? '#a8e6cf'
        : status === 'Completed'
          ? '#27ae60'
          : status === 'Canceled'
            ? '#e74c3c'
            : '#95a5a6',
  color: 'white',
  padding: '4px 12px',
  borderRadius: '16px',
  fontSize: '12px',
  fontWeight: '600',
  textTransform: 'uppercase' as const,
  textAlign: 'center' as const,
})

export const getLabelStyle = () => ({
  fontWeight: '600',
  color: '#7f8c8d',
  minWidth: '80px',
})

export const getLabelMobileStyle = () => ({
  fontWeight: '600',
  color: '#7f8c8d',
  minWidth: 'auto',
  fontSize: '13px',
})

export const getValueStyle = (
  type: 'default' | 'email' | 'monospace' | 'large' = 'default',
  adminComments?: string[],
) => {
  const comments = adminComments ?? []
  if (comments.length > 0 && type === 'default') {
    return {
      fontSize: '12px',
      overflowY: 'auto' as const,
    }
  }

  const baseStyle = {
    fontSize: '16px',
    fontWeight: '500',
  }

  switch (type) {
    case 'email':
      return {
        ...baseStyle,
        color: '#3498db',
        textDecoration: 'underline',
      }
    case 'monospace':
      return {
        ...baseStyle,
        fontFamily: 'monospace',
        fontSize: '18px',
      }
    case 'large':
      return {
        ...baseStyle,
        fontSize: '18px',
      }
    default:
      return {
        ...baseStyle,
        color: '#2c3e50',
      }
  }
}

export const getValueMobileStyle = (
  type: 'default' | 'email' | 'monospace' | 'large' = 'default',
  adminComments?: string[],
) => {
  const comments = adminComments ?? []
  if (comments.length > 0 && type === 'default') {
    return {
      fontSize: '11px',
      overflowY: 'auto' as const,
    }
  }

  const baseStyle = {
    fontSize: '14px',
    fontWeight: '500',
  }

  switch (type) {
    case 'email':
      return {
        ...baseStyle,
        color: '#3498db',
        textDecoration: 'underline',
      }
    case 'monospace':
      return {
        ...baseStyle,
        fontFamily: 'monospace',
        fontSize: '16px',
      }
    case 'large':
      return {
        ...baseStyle,
        fontSize: '16px',
      }
    default:
      return {
        ...baseStyle,
        color: '#2c3e50',
      }
  }
}

export const getRowStyle = (alignItems: 'center' | 'flex-start' = 'center') => ({
  margin: '0 0 20px 0',
  fontSize: '16px',
  display: 'flex',
  alignItems,
  gap: '10px',
})

export const getRowMobileStyle = (alignItems: 'center' | 'flex-start' = 'center') => ({
  margin: '0 0 15px 0',
  fontSize: '14px',
  display: 'flex',
  alignItems,
  gap: '8px',
  flexDirection: 'column' as const,
})

export const getContainerStyle = () => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: '30px',
  gap: '10px',
})

export const getCloseButtonStyle = () => ({
  position: 'absolute' as const,
  bottom: '20px',
  right: '20px',
  padding: '10px 20px',
  backgroundColor: 'black',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: '500',
  transition: 'all 0.2s ease',
})

export const getHomeButtonStyle = () => ({
  position: 'fixed' as const,
  bottom: '20px',
  right: '20px',
  padding: '10px 20px',
  backgroundColor: 'black',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: '500',
  transition: 'all 0.2s ease',
})

export const getAdminCommentSaveButtonStyle = () => ({
  padding: '6px 12px',
  backgroundColor: 'white',
  color: 'black',
  border: '1px solid black',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '12px',
  fontWeight: '500',
  transition: 'all 0.2s ease',
  whiteSpace: 'nowrap',
  '&:hover': {
    backgroundColor: '#f8f9fa',
    borderColor: '#0056b3',
  },
})

export const getAdminCommentTextareaStyle = () => ({
  resize: 'none' as const,
  overflow: 'hidden',
  width: '200px',
  height: '100px',
})

export const getModalContentStyle = () => ({
  backgroundColor: 'rgb(245, 245, 245)',
  border: '1px solid rgb(213, 206, 196)',
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  position: 'fixed' as const,
  padding: '10px',
  top: 'calc(50vh - 350px)',
  left: 'calc(50% - 350px)',
  zIndex: 9999,
  width: '700px',
  height: '700px',
  overflow: 'auto',
})

export const getModalContentMobileStyle = () => ({
  backgroundColor: 'rgb(245, 245, 245)',
  border: '1px solid rgb(213, 206, 196)',
  borderRadius: '8px',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
  position: 'fixed' as const,
  padding: '8px',
  top: '5vh',
  left: '5vw',
  right: '5vw',
  bottom: '5vh',
  zIndex: 9999,
  minWidth: 'auto',
  minHeight: 'auto',
  maxHeight: '90vh',
  overflow: 'auto',
})

export const getModalContainerStyle = () => ({
  padding: '30px',
  minHeight: '300px',
  color: '#2c3e50',
  backgroundColor: '#ffffff',
  borderRadius: '8px',
  position: 'relative' as const,
})

export const getModalContainerMobileStyle = () => ({
  padding: '20px',
  minHeight: 'auto',
  color: '#2c3e50',
  backgroundColor: '#ffffff',
  borderRadius: '6px',
  position: 'relative' as const,
})

export const getModalTitleStyle = () => ({
  margin: '0 0 30px 0',
  color: '#34495e',
  fontSize: '16px',
  fontWeight: '600',
  borderBottom: '2px solid #ecf0f1',
  paddingBottom: '15px',
})

export const getModalTitleMobileStyle = () => ({
  margin: '0 0 20px 0',
  color: '#34495e',
  fontSize: '14px',
  fontWeight: '600',
  borderBottom: '2px solid #ecf0f1',
  paddingBottom: '10px',
})

export const getTwoColumnContainerStyle = () => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  marginBottom: '30px',
  gap: '10px',
})

export const getTwoColumnContainerMobileStyle = () => ({
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'flex-start',
  marginBottom: '20px',
  gap: '15px',
})

export const getColumnStyle = () => ({
  flex: 1,
})

export const getColumnMobileStyle = () => ({
  flex: 'none',
  width: '100%',
})

export const getDescriptionSectionStyle = () => ({
  margin: '0 0 20px 0',
  fontSize: '16px',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px',
})

export const getDescriptionTextStyle = () => ({
  color: '#2c3e50',
  fontSize: '16px',
  fontWeight: '500',
  flex: 1,
  lineHeight: '1.5',
})

export const theme = createTheme({
  colors: {
    yellow: [
      '#fff3cd',
      '#ffeaa7',
      '#fdcb6e',
      '#f39c12',
      '#e17055',
      '#d63031',
      '#b71540',
      '#a29bfe',
      '#6c5ce7',
      '#00b894',
    ],
    green: [
      '#d1f2eb',
      '#a8e6cf',
      '#7bed9f',
      '#27ae60',
      '#00b894',
      '#00a085',
      '#008f7a',
      '#00806f',
      '#007164',
      '#006259',
    ],
    red: ['#ffebee', '#ffcdd2', '#ef9a9a', '#e74c3c', '#e53e3e', '#d32f2f', '#c62828', '#b71c1c', '#a00000', '#8b0000'],
    gray: [
      '#f8f9fa',
      '#e9ecef',
      '#dee2e6',
      '#ced4da',
      '#adb5bd',
      '#6c757d',
      '#495057',
      '#343a40',
      '#212529',
      '#000000',
    ],
  },
})

export const getAdminCommentTextareaMobileStyle = () => ({
  resize: 'none' as const,
  overflow: 'hidden',
  width: '100%',
  height: '80px',
})

export const getAdminCommentSaveButtonMobileStyle = () => ({
  padding: '8px 16px',
  backgroundColor: 'white',
  color: 'black',
  border: '1px solid black',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: '500',
  transition: 'all 0.2s ease',
  whiteSpace: 'nowrap',
  width: '100%',
  '&:hover': {
    backgroundColor: '#f8f9fa',
    borderColor: '#0056b3',
  },
})

export const getCloseButtonMobileStyle = () => ({
  position: 'absolute' as const,
  bottom: '15px',
  right: '15px',
  padding: '8px 16px',
  backgroundColor: 'black',
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: '500',
  transition: 'all 0.2s ease',
})

export const getCopyButtonStyle = () => ({
  padding: '8px 16px',
  backgroundColor: 'white',
  color: '#3498db',
  // border: '1px solid #3498db',
  // borderRadius: '6px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: '500',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#f8f9fa',
    borderColor: '#2980b9',
  },
})

export const getCopyButtonMobileStyle = () => ({
  padding: '6px 12px',
  backgroundColor: 'white',
  color: '#3498db',
  // border: '1px solid #3498db',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: '12px',
  fontWeight: '500',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#f8f9fa',
    borderColor: '#2980b9',
  },
})
